import type { ComponentProps, ComponentPropsWithoutRef } from "react";
import type { Control } from "react-hook-form";
import { Link as LinkPrimitive } from "react-router-dom";
import { jt, t } from "ttag";

import { EXTERNAL_LINK } from "@/config/external-links";
import { Checkbox, Form } from "@/shared/ui";
import { tw } from "@/shared/utils";

const Link = ({ ...props }: ComponentProps<typeof LinkPrimitive>) => {
  return (
    <LinkPrimitive
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
      {...props}
    />
  );
};

interface FieldValue {
  termsAndConditionsConsent?: boolean;
}

type TermsAndConditionsFieldControl<T> = T extends FieldValue
  ? Control<T>
  : never;

type TermsAndConditionsFieldProps<T extends FieldValue> =
  ComponentPropsWithoutRef<typeof Form.Item> & {
    control: TermsAndConditionsFieldControl<T>;
    labelClassName?: string;
  };
export const TermsAndConditionsField = <T extends FieldValue>({
  control,
  labelClassName,
  ...props
}: TermsAndConditionsFieldProps<T>) => {
  const termsAndConditionsLink = (
    <Link
      key="terms-and-conditions-link"
      to={EXTERNAL_LINK.TERMS_AND_CONDITIONS}
    >
      {t`Terms & Conditions`}
    </Link>
  );

  const telehealthConsentLink = (
    <Link
      key="telehealth-consent-link"
      to={EXTERNAL_LINK.OPENLOOP_TELEHEALTH_CONSENT}
    >
      {t`Telehealth Consent`}
    </Link>
  );

  return (
    <Form.Field
      control={control}
      name="termsAndConditionsConsent"
      render={({ field: { value, onChange, ...rest } }) => (
        <Form.Item className="mb-8 w-full" {...props}>
          <Form.Control>
            <Checkbox.Item checked={value} onCheckedChange={onChange} {...rest}>
              <Checkbox.Label className={tw(labelClassName)}>
                {jt`I understand and agree to LevelsRx's ${termsAndConditionsLink} and ${telehealthConsentLink}.`}
              </Checkbox.Label>
            </Checkbox.Item>
          </Form.Control>

          <Form.Message />
        </Form.Item>
      )}
    />
  );
};
